import { useEffect, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDismissAppAnnouncement, usePendingAppAnnouncementState } from '@/features/users';
import { getErrorCopy } from '@/shared/copy/errors';
import { isEntryAuthFlowPath } from '@/shared/hooks/useAppAuthBootstrapFlow';
import {
  AppAnnouncementModal,
  teamInviteAfterFirstUploadAnnouncementKey,
  welcomePostOnboardingAnnouncementKey,
} from './AppAnnouncementModal';
import type { AuthSession } from '@/auth/types';

type AppAnnouncementControllerProps = {
  activePath: string;
  forceWelcomeAnnouncement?: boolean;
  planUserLimit: number | null;
  session: AuthSession | null;
};

const debugAnnouncementKeys = [
  welcomePostOnboardingAnnouncementKey,
  teamInviteAfterFirstUploadAnnouncementKey,
] as const;

const enabledAnnouncementKeys = new Set<string>(debugAnnouncementKeys);

const resolveDebugAnnouncementKey = (searchParams: URLSearchParams): string | null => {
  const announcementKey = searchParams.get('announcement');
  if (
    import.meta.env.DEV &&
    debugAnnouncementKeys.includes(announcementKey as (typeof debugAnnouncementKeys)[number])
  ) {
    return announcementKey;
  }
  return null;
};

export const AppAnnouncementController = ({
  activePath,
  forceWelcomeAnnouncement = false,
  planUserLimit,
  session,
}: AppAnnouncementControllerProps): ReactElement | null => {
  const [searchParams] = useSearchParams();
  const debugAnnouncementKey = resolveDebugAnnouncementKey(searchParams);
  const [open, setOpen] = useState(false);
  const [debugOpenConsumed, setDebugOpenConsumed] = useState(false);
  const [forcedOpenConsumed, setForcedOpenConsumed] = useState(false);
  const [dismissError, setDismissError] = useState<string | null>(null);
  const isEntryAuthFlow = isEntryAuthFlowPath(activePath);
  const enabled = session?.onboardingCompleted === true && !isEntryAuthFlow;
  const pendingAnnouncementQuery = usePendingAppAnnouncementState({ enabled });
  const dismissAppAnnouncement = useDismissAppAnnouncement();
  const rawPendingAnnouncementKey = pendingAnnouncementQuery.data?.pendingAnnouncementKey ?? null;
  const pendingAnnouncementKey =
    rawPendingAnnouncementKey && enabledAnnouncementKeys.has(rawPendingAnnouncementKey)
      ? rawPendingAnnouncementKey
      : null;
  const forcedAnnouncementKey = forceWelcomeAnnouncement
    ? welcomePostOnboardingAnnouncementKey
    : null;
  const activeAnnouncementKey =
    debugAnnouncementKey ?? pendingAnnouncementKey ?? forcedAnnouncementKey;

  useEffect(() => {
    setDismissError(null);
  }, [activeAnnouncementKey]);

  useEffect(() => {
    if (!debugAnnouncementKey) {
      setDebugOpenConsumed(false);
    }
  }, [debugAnnouncementKey]);

  useEffect(() => {
    if (!forceWelcomeAnnouncement) {
      setForcedOpenConsumed(false);
    }
  }, [forceWelcomeAnnouncement]);

  useEffect(() => {
    if (!session || isEntryAuthFlow) {
      setOpen(false);
      return;
    }
    if (debugAnnouncementKey && !debugOpenConsumed) {
      setOpen(true);
      setDebugOpenConsumed(true);
      return;
    }
    if (pendingAnnouncementKey) {
      setOpen(true);
      return;
    }
    if (forcedAnnouncementKey && !forcedOpenConsumed) {
      setOpen(true);
      setForcedOpenConsumed(true);
    }
  }, [
    debugAnnouncementKey,
    debugOpenConsumed,
    forcedAnnouncementKey,
    forcedOpenConsumed,
    isEntryAuthFlow,
    pendingAnnouncementKey,
    session,
  ]);

  const handleClose = (): void => {
    if (dismissAppAnnouncement.isPending) return;
    if (debugAnnouncementKey) {
      setOpen(false);
      return;
    }
    const dismissibleAnnouncementKey = pendingAnnouncementKey ?? forcedAnnouncementKey;
    if (dismissibleAnnouncementKey) {
      setDismissError(null);
      dismissAppAnnouncement.mutate(dismissibleAnnouncementKey, {
        onSuccess: () => {
          setOpen(false);
        },
        onError: () => {
          setDismissError(
            getErrorCopy('mutation.submitFailed', { target: 'announcement dismissal' }),
          );
        },
      });
      return;
    }
    setOpen(false);
  };

  if (!session || isEntryAuthFlow || !activeAnnouncementKey) {
    return null;
  }

  return (
    <AppAnnouncementModal
      announcementKey={activeAnnouncementKey}
      open={open}
      planUserLimit={planUserLimit}
      dismissError={dismissError}
      dismissing={dismissAppAnnouncement.isPending}
      onClose={handleClose}
    />
  );
};
